import React, { useState, useEffect } from 'react'
import { Container, Toast as toast, SubmitDialog } from 'components'
import {
  Box,
  Grid,
  Paper,
  Typography,
  CircularProgress,
  IconButton,
  InputBase,
  Button,
} from '@mui/material'
import { ResponsivePie } from '@nivo/pie'
import { ResponsiveBar } from '@nivo/bar'
import styles from './heartbeat.module.scss'
import EditIcon from '@mui/icons-material/Edit'
import SaveIcon from '@mui/icons-material/Save'
import CarrierHeartbeatVehiclesTable from './carrierHeartbeatVehiclesTable'
import {
  getVehiclePolicyGraphicsData,
  getHeartbeatVehicles,
  uploadVehiclePolicyCsv,
  getGpsMilesData,
  addVehicleToPolicy,
  clearVehiclePolicy,
  pauseVehicle,
  getVehicleActiveData,
} from 'api'
import {
  HeartbeatVehicle,
  VehiclePolicyGraphicsData,
  GpsMilesRange,
  GpsMilesData,
  MappedHeartbeatVehicle,
  VehicleActiveData,
} from 'interfaces/ICarrierHeartbeat'
import { reportException } from 'tools/logs'
import { formatDate } from 'tools/helper'
import strings from 'strings'

const s = strings.CARRIER_HEARTBEAT
const t = strings.TABLE_DEFAULTS

interface IHeartbeatDashboardProps {}

interface DialogConfig {
  type: 'success' | 'warning' | 'delete' | 'none'
  title: string
  body1: string
  body2?: string
  action: () => Promise<void>
  primaryBtnProps?: {
    label: string
    color?: 'primary' | 'error'
  }
}

const HeartbeatDashboard: React.FC<IHeartbeatDashboardProps> = () => {
  const carrierId: any = localStorage.getItem('carrierId') || '1' // Default to 1 if not available
  const [loading, setLoading] = useState(true)

  const [graphicsData, setGraphicsData] =
    useState<VehiclePolicyGraphicsData | null>(null)
  const [activeVehiclesData, setActiveVehiclesData] =
    useState<VehicleActiveData | null>(null)
  const [gpsMilesData, setGpsMilesData] = useState<GpsMilesData | null>(null)
  const [heartbeatVehiclesData, setHeartbeatVehiclesData] = useState<
    HeartbeatVehicle[]
  >([])
  const [mappedHeartbeatVehiclesData, setMappedHeartbeatVehiclesData] =
    useState<MappedHeartbeatVehicle[]>([])

  const [isLoadingGraphicsData, setIsLoadingGraphicsData] = useState(false)
  const [isLoadingActiveVehiclesData, setIsLoadingActiveVehiclesData] =
    useState(false)
  const [isGpsMilesLoading, setIsGpsMilesLoading] = useState(false)
  const [isHeartbeatVehiclesLoading, setIsHeartbeatVehiclesLoading] =
    useState(false)

  const [hasGraphicsData, setHasGraphicsData] = useState(false)
  const [hasActiveVehiclesData, setHasActiveVehiclesData] = useState(false)
  const [hasGpsMilesData, setHasGpsMilesData] = useState(false)
  const [hasHeartbeatVehiclesData, setHasHeartbeatVehiclesData] =
    useState(false)

  const [showDialog, setShowDialog] = useState(false)
  const [dialogLoading, setDialogLoading] = useState(false)
  const [dialogConfig, setDialogConfig] = useState<DialogConfig | null>(null)
  const [dialogContent, setDialogContent] = useState<React.ReactNode>(null)

  const [isEditingVehicles, setIsEditingVehicles] = useState(false)
  const [editedVehiclesOnPolicy, setEditedVehiclesOnPolicy] =
    useState<number>(0)
  const [totalActiveVehicles, setTotalActiveVehicles] = useState<number>(0)

  const [periodFilter, setPeriodFilter] = useState<string>('any')

  useEffect(() => {
    callGetVehiclePolicyGraphicsData()
    callGetVehicleActiveData()
    callGetGpsMilesData()
    callGetHeartbeatVehicles(1)
  }, [])

  useEffect(() => {
    mapHeartbeatVehiclesData()
  }, [periodFilter, heartbeatVehiclesData])

  const callGetVehiclePolicyGraphicsData = async () => {
    setIsLoadingGraphicsData(true)
    try {
      const res = await getVehiclePolicyGraphicsData(carrierId)
      if (res) {
        setGraphicsData(res)
        setHasGraphicsData(res.vehiclesOnPolicy > 0)
      } else {
        setGraphicsData(null)
        setHasGraphicsData(false)
      }
    } catch (err) {
      setHasGraphicsData(false)
      reportException(err)
    } finally {
      setLoading(false)
      setIsLoadingGraphicsData(false)
    }
  }

  const callGetVehicleActiveData = async () => {
    setIsLoadingActiveVehiclesData(true)
    try {
      const res = await getVehicleActiveData(carrierId)
      if (res) {
        setActiveVehiclesData(res)
        setHasActiveVehiclesData(true)
      } else {
        setActiveVehiclesData(null)
        setHasActiveVehiclesData(false)
      }
    } catch (err) {
      setHasActiveVehiclesData(false)
      reportException(err)
    } finally {
      setIsLoadingActiveVehiclesData(false)
    }
  }

  const callGetGpsMilesData = async () => {
    setIsGpsMilesLoading(true)
    try {
      const res = await getGpsMilesData(carrierId)
      if (res && res.vehicleMilesRange.length > 0) {
        setGpsMilesData(res)
        setHasGpsMilesData(true)
      } else {
        setGpsMilesData(null)
        setHasGpsMilesData(false)
      }
    } catch (err) {
      setHasGpsMilesData(false)
      reportException(err)
    } finally {
      setIsGpsMilesLoading(false)
    }
  }

  const callGetHeartbeatVehicles = async (active: number) => {
    setIsHeartbeatVehiclesLoading(true)
    try {
      const res = await getHeartbeatVehicles(carrierId, active)
      if (res && res.length > 0) {
        setHeartbeatVehiclesData(res)
        setHasHeartbeatVehiclesData(true)
        if (active === 1) {
          setTotalActiveVehicles(res.length)
        }
      } else {
        setHeartbeatVehiclesData([])
      }
      mapHeartbeatVehiclesData()
    } catch (err) {
      setHasHeartbeatVehiclesData(false)
      reportException(err)
    } finally {
      setIsHeartbeatVehiclesLoading(false)
    }
  }

  const callUploadCsv = async (file: File, isActive: number) => {
    uploadVehiclePolicyCsv(carrierId, file)
      .then((res: any) => {
        reloadAllData(isActive)
        toast({ id: 'uploadCsvToast', title: res?.message })
      })
      .catch((err: any) => {
        reportException(err)
      })
  }

  const callAddVehicleToPolicy = async (
    identificationNoList: string[],
    isActive: number
  ) => {
    const filteredIdentificationNoList = identificationNoList.filter(
      (id) => id !== t.DASH_VALUE
    )
    const payload = {
      identificationNoList:
        filteredIdentificationNoList.length > 0
          ? filteredIdentificationNoList
          : [],
    }
    addVehicleToPolicy(carrierId, payload)
      .then((res: any) => {
        reloadAllData(isActive)
        toast({ id: 'addVehicleToPolicyToast', title: res?.message })
      })
      .catch((err: any) => {
        reportException(err)
      })
  }

  const callClearVehiclePolicy = async (
    identificationNoList: string[],
    isActive: number
  ) => {
    const filteredIdentificationNoList = identificationNoList.filter(
      (id) => id !== t.DASH_VALUE
    )
    const payload = {
      identificationNoList:
        filteredIdentificationNoList.length > 0
          ? filteredIdentificationNoList
          : [],
    }
    clearVehiclePolicy(carrierId, payload)
      .then((res: any) => {
        reloadAllData(isActive)
        toast({ id: 'clearVehiclePolicyToast', title: res?.message })
      })
      .catch((err: any) => {
        reportException(err)
      })
  }

  const callPauseVehicle = async (
    identificationNoList: string[],
    assetEldIdList: (number | string)[],
    isActive: number
  ) => {
    const filteredIdentificationNoList = identificationNoList.filter(
      (id) => id !== t.DASH_VALUE
    )
    const filteredAssetEldIdList = assetEldIdList.filter(
      (id) => id !== t.DASH_VALUE
    )
    const payload = {
      identificationNoList:
        filteredIdentificationNoList.length > 0
          ? filteredIdentificationNoList
          : [],
      assetEldIdList:
        filteredAssetEldIdList.length > 0 ? filteredAssetEldIdList : [],
    }
    pauseVehicle(carrierId, payload)
      .then((res: any) => {
        reloadAllData(isActive)
        toast({ id: 'pauseVehicleToast', title: res?.message })
      })
      .catch((err: any) => {
        reportException(err)
      })
  }

  const reloadAllData = (isActive: number) => {
    callGetHeartbeatVehicles(isActive)
    callGetVehiclePolicyGraphicsData()
    callGetVehicleActiveData()
    callGetGpsMilesData()
  }

  const handleDialogConfirm = async () => {
    if (!dialogConfig) return

    setDialogLoading(true)
    try {
      await dialogConfig.action()
      setDialogLoading(false)
      setShowDialog(false)
    } catch (err) {
      setDialogLoading(false)
      reportException(err)
    }
  }

  const handleShowDialog = (
    config: DialogConfig,
    contentNode?: React.ReactNode
  ) => {
    setDialogConfig(config)
    setDialogContent(contentNode)
    setShowDialog(true)
  }

  const handleEditVehiclesClick = () => {
    handleShowDialog({
      type: 'warning',
      title: 'Edit Vehicles on Policy?',
      body1:
        'We calculate the vehicles on policy based on your CSV upload. Are you sure you want to edit that number?',
      action: async () => {
        setIsEditingVehicles(true)
        setEditedVehiclesOnPolicy(graphicsData?.vehiclesOnPolicy || 0)
        return Promise.resolve()
      },
      primaryBtnProps: {
        label: 'Continue',
        color: 'primary',
      },
    })
  }

  const handleSaveVehicles = () => {
    if (graphicsData) {
      setGraphicsData({
        ...graphicsData,
        vehiclesOnPolicy: editedVehiclesOnPolicy,
      })
    }
    setIsEditingVehicles(false)
  }

  const downloadTemplate = () => {
    const csvContent = s.CSV_TEMPLATE
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', 'vehicle_template.csv')
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const toolbarActions = {
    uploadCsv: callUploadCsv,
    showDialog: handleShowDialog,
    downloadTemplate: downloadTemplate,
    addVehicle: callAddVehicleToPolicy,
    clearVehicle: callClearVehiclePolicy,
    pauseVehicle: callPauseVehicle,
    setPeriodFilter: setPeriodFilter,
  }

  const mapHeartbeatVehiclesData = () => {
    const mappedList = heartbeatVehiclesData?.map((vehicle) => ({
      rowId: vehicle?.rowId,
      assetEldId: vehicle?.assetEldId || t.DASH_VALUE,
      identificationNo: vehicle?.identificationNo || t.DASH_VALUE,
      policyList: vehicle?.policyList,
      telematics: vehicle?.telematics,
      lastTracking: formatDate(vehicle?.lastTracking) || t.DASH_VALUE,
      lastMovement: formatDate(vehicle?.lastMovement) || t.DASH_VALUE,
      miles:
        periodFilter === '60'
          ? vehicle?.movingIn60Days
            ? vehicle?.miles60days
            : t.DASH_VALUE
          : periodFilter === '90'
          ? vehicle?.movingIn90Days
            ? vehicle?.miles90days
            : t.DASH_VALUE
          : vehicle?.movingIn30Days
          ? vehicle?.miles30days
          : t.DASH_VALUE,
      pullingEnabled: vehicle?.pullingEnabled,
      movingIn30Days: vehicle?.movingIn30Days,
      movingIn60Days: vehicle?.movingIn60Days,
      movingIn90Days: vehicle?.movingIn90Days,
      vehicleClass: vehicle?.vehicleClass || t.DASH_VALUE,
      vehicleMake: vehicle?.vehicleMake || t.DASH_VALUE,
      vehicleModel: vehicle?.vehicleModel || t.DASH_VALUE,
      vehicleYear: vehicle?.vehicleYear || t.DASH_VALUE,
    }))
    setMappedHeartbeatVehiclesData(mappedList || [])
  }

  const calculateReportingTelematicsForGraph = () => {
    if (
      !graphicsData?.vehiclesOnPolicy ||
      !graphicsData?.vehiclesOnPolicyActive
    ) {
      return 0
    }
    return Math.round(
      (graphicsData?.vehiclesOnPolicyActive / graphicsData?.vehiclesOnPolicy) *
        100
    )
  }

  const calculateReportingTelematics = () => {
    if (!graphicsData?.vehiclesOnPolicy || !graphicsData?.activeVehicles) {
      return 0
    }
    return Math.round(
      (graphicsData?.vehiclesOnPolicy / graphicsData?.activeVehicles) * 100
    )
  }

  const createTelematicStatusGraphData = () => {
    return [
      {
        id: 'Reporting',
        label: 'Reporting',
        value: graphicsData?.vehiclesOnPolicyActive || 0,
        color: '#0088E1',
      },
      {
        id: 'Not on Telematics',
        label: 'Not on Telematics',
        value:
          (graphicsData?.vehiclesOnPolicy || 0) -
          (graphicsData?.vehiclesOnPolicyActive || 0),
        color: '#B0B7C3',
      },
    ]
  }

  const createGpsMilesGraphData = () => {
    return (
      gpsMilesData?.vehicleMilesRange?.map((item: GpsMilesRange) => ({
        range: item.range,
        count: item.count,
      })) || []
    )
  }

  if (loading) {
    return (
      <Container className={styles.heartbeatContainer}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '80vh',
          }}
        >
          <CircularProgress size={48} sx={{ mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            Loading Heartbeat Data...
          </Typography>
        </Box>
      </Container>
    )
  }

  return (
    <Container className={styles.heartbeatContainer}>
      <Box sx={{ pt: 3 }}>
        {/* Metrics Cards */}
        <Box sx={{ px: 3, mb: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <Paper elevation={0} className={styles.metricCard}>
                <div className={styles.metricLabel}>Vehicles On Policy</div>
                <div
                  className={styles.metricValue}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {isEditingVehicles ? (
                    <>
                      <InputBase
                        value={editedVehiclesOnPolicy}
                        onChange={(e) => {
                          const value = parseInt(e.target.value)
                          if (!isNaN(value) && value >= 0) {
                            setEditedVehiclesOnPolicy(value)
                          }
                        }}
                        type="number"
                        sx={{
                          fontSize: 'inherit',
                          width: '80px',
                          textAlign: 'center',
                          '& input': {
                            textAlign: 'center',
                          },
                        }}
                      />
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSaveVehicles}
                        startIcon={<SaveIcon />}
                        sx={{ ml: 1, textTransform: 'none' }}
                      >
                        Save
                      </Button>
                    </>
                  ) : (
                    <>
                      {graphicsData?.vehiclesOnPolicy || 0}
                      <IconButton
                        size="small"
                        sx={{ ml: 1 }}
                        onClick={handleEditVehiclesClick}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </>
                  )}
                </div>
                <div className={styles.metricSubtitle}>
                  {graphicsData?.activeVehicles || 0} active (
                  {calculateReportingTelematics()}%)
                </div>
              </Paper>
            </Grid>
            <Grid item xs={4}>
              <Paper elevation={0} className={styles.metricCard}>
                <div className={styles.metricLabel}>Active Vehicles</div>
                <div className={styles.metricValue}>
                  {activeVehiclesData?.activeVehicles || 0}
                </div>
                <div className={styles.metricSubtitle}>
                  Out of {activeVehiclesData?.totalActiveVehicles || 0} total
                  vehicles
                </div>
              </Paper>
            </Grid>
            <Grid item xs={4}>
              <Paper elevation={0} className={styles.metricCard}>
                <div className={styles.metricLabel}>Miles (Last 30 Days)</div>
                <div className={styles.metricValue}>
                  {Math.trunc(
                    Math.abs(gpsMilesData?.totalMiles || 0)
                  ).toLocaleString()}
                </div>
                <div className={styles.metricSubtitle}>
                  Across all tracked vehicles
                </div>
              </Paper>
            </Grid>
          </Grid>
        </Box>
        {/* Charts */}
        <Box sx={{ px: 3, mb: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Paper
                elevation={0}
                className={styles.chartCard}
                sx={{ height: 340 }}
              >
                <div
                  className={styles.chartTitle}
                  style={{ textAlign: 'center' }}
                >
                  Vehicle Telematics Status (
                  {calculateReportingTelematicsForGraph()}% of Policy)
                </div>
                <div style={{ height: 260, marginBottom: 30 }}>
                  {isLoadingGraphicsData ? (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%',
                        textAlign: 'center',
                      }}
                    >
                      <CircularProgress size={40} sx={{ mb: 2 }} />
                      <Typography variant="body2" color="text.secondary">
                        Loading graph data...
                      </Typography>
                    </Box>
                  ) : hasGraphicsData ? (
                    <ResponsivePie
                      data={createTelematicStatusGraphData()}
                      margin={{ top: 30, right: 60, bottom: 80, left: 60 }}
                      innerRadius={0.7}
                      padAngle={2}
                      cornerRadius={6}
                      colors={({ data }) => data.color}
                      borderWidth={2}
                      borderColor={{
                        from: 'color',
                        modifiers: [['darker', 0.2]],
                      }}
                      enableArcLabels={false}
                      arcLinkLabelsSkipAngle={10}
                      arcLinkLabelsTextColor="#555"
                      arcLinkLabelsThickness={2}
                      arcLinkLabelsColor={{ from: 'color' }}
                      legends={[
                        {
                          anchor: 'bottom',
                          direction: 'row',
                          justify: false,
                          translateY: 50,
                          itemsSpacing: 10,
                          itemWidth: 120,
                          itemHeight: 18,
                          itemTextColor: '#555',
                          symbolSize: 18,
                          symbolShape: 'circle',
                        },
                      ]}
                    />
                  ) : (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%',
                        textAlign: 'center',
                      }}
                    >
                      <Typography variant="body1" color="text.secondary">
                        No data available
                      </Typography>
                    </Box>
                  )}
                </div>
              </Paper>
            </Grid>
            <Grid item xs={6}>
              <Paper elevation={0} className={styles.chartCard}>
                <div
                  className={styles.chartTitle}
                  style={{ textAlign: 'center' }}
                >
                  GPS Miles by Vehicle
                </div>
                <div style={{ height: 240 }}>
                  {isGpsMilesLoading ? (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%',
                        textAlign: 'center',
                      }}
                    >
                      <CircularProgress size={40} sx={{ mb: 2 }} />
                      <Typography variant="body2" color="text.secondary">
                        Loading graph data...
                      </Typography>
                    </Box>
                  ) : hasGpsMilesData ? (
                    <ResponsiveBar
                      data={createGpsMilesGraphData()}
                      keys={['count']}
                      indexBy="range"
                      margin={{ top: 30, right: 30, bottom: 50, left: 60 }}
                      padding={0.3}
                      colors={['#00B386']}
                      borderRadius={4}
                      axisBottom={{
                        tickSize: 5,
                        tickPadding: 5,
                        tickRotation: 0,
                        legend: 'Miles',
                        legendPosition: 'middle',
                        legendOffset: 36,
                      }}
                      axisLeft={{
                        tickSize: 5,
                        tickPadding: 5,
                        tickRotation: 0,
                        legend: 'Vehicles',
                        legendPosition: 'middle',
                        legendOffset: -50,
                      }}
                      enableLabel={false}
                      tooltip={({ id, value, indexValue }) => (
                        <Box
                          sx={{
                            p: 1,
                            bgcolor: 'background.paper',
                            boxShadow: 1,
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'divider',
                          }}
                        >
                          <Typography variant="body2" fontWeight={600}>
                            {indexValue}
                          </Typography>
                          <Typography variant="caption">
                            {value} vehicles
                          </Typography>
                        </Box>
                      )}
                      theme={{
                        axis: {
                          legend: { text: { fontSize: 12, fill: '#777' } },
                          ticks: { text: { fontSize: 12, fill: '#777' } },
                        },
                        grid: { line: { stroke: '#e0e0e0', strokeWidth: 1 } },
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%',
                        textAlign: 'center',
                      }}
                    >
                      <Typography variant="body1" color="text.secondary">
                        No data available
                      </Typography>
                    </Box>
                  )}
                </div>
                {hasGpsMilesData && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      mt: 1,
                      mb: 2,
                      display: 'block',
                      textAlign: 'center',
                    }}
                  >
                    Last 30-day mileage distribution across all{' '}
                    {gpsMilesData?.totalVehicles || 0} vehicles
                  </Typography>
                )}
              </Paper>
            </Grid>
          </Grid>
        </Box>
        <CarrierHeartbeatVehiclesTable
          loading={isHeartbeatVehiclesLoading}
          reloadVehicles={callGetHeartbeatVehicles}
          vehiclesList={mappedHeartbeatVehiclesData}
          toolbarActions={toolbarActions}
          pauseVehicle={callPauseVehicle}
          removeVehicle={callClearVehiclePolicy}
          addVehicle={callAddVehicleToPolicy}
          showDialog={handleShowDialog}
          periodFilter={periodFilter}
        />
      </Box>
      {dialogConfig && (
        <SubmitDialog
          cancelId="cancelHeartbeatAction"
          open={showDialog}
          handleClose={() => setShowDialog(false)}
          title={dialogConfig.title}
          body1={dialogConfig.body1}
          body2={dialogConfig.body2}
          type={dialogConfig.type}
          primaryBtnProps={{
            id: 'confirmHeartbeatAction',
            label: dialogConfig.primaryBtnProps?.label || 'Confirm',
            onClick: handleDialogConfirm,
            loading: dialogLoading,
            disabled: dialogLoading,
            color: dialogConfig.primaryBtnProps?.color,
          }}
          contentNode={dialogContent}
        />
      )}
    </Container>
  )
}

export default HeartbeatDashboard
